import React, { useRef, useEffect, useState } from "react";
import { COLORS } from "../../assets/colors";
import { MEDIAS } from '../../assets/medias';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  Avatar,
  Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip,
  Alert,
  Snackbar
  // Divider
} from '@mui/material';
import * as XLSX from 'xlsx';
import TuneIcon from '@mui/icons-material/Tune';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CodeIcon from '@mui/icons-material/Code';
import CodeOffIcon from '@mui/icons-material/CodeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CustomSkeleton from "../../sharedComponents/CustomSkeleton";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeMirror from "@uiw/react-codemirror";
import { sql } from "@codemirror/lang-sql";
import { materialDark } from "@uiw/codemirror-theme-material";
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
// import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
// import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { keyframes } from "@mui/system"


const ToolbarButton = ({ icon, tooltip, _func }) => (
  <Tooltip title={tooltip}>
    <IconButton sx={{ color: COLORS.GRAY_ICONS }} onClick={_func}>
      {icon}
    </IconButton>
  </Tooltip>
);

const Toolbar = ({ onSendMessage, userMessage, handleCopyToClipboard }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        // padding: 1,
        pt: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: 'none'
      }}
    >
      <ToolbarButton icon={<ThumbUpOutlinedIcon />} tooltip="J'aime" />
      <ToolbarButton icon={<ThumbDownOutlinedIcon />} tooltip="Je n'aime pas" />
      <ToolbarButton icon={<ContentCopyIcon />} tooltip="Copier" _func={handleCopyToClipboard} />
      <ToolbarButton icon={<AutorenewIcon />} tooltip="Régénérer" _func={() => onSendMessage(userMessage)} />
    </Box>
  );

};

const QueryEditorModal = ({ initialQuery, onUpdate, open, onClose, onSendMessage, handleCallEdition }) => {
  const [tempQuery, setTempQuery] = useState(initialQuery);
  const handleSave = () => {
    // onSendMessage(tempQuery)
    // onUpdate(tempQuery);
    handleCallEdition()
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" slotProps={{
      paper: {
        sx: {
          borderRadius: 5,
          overflow: 'hidden',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        }
      }
    }}>
      <DialogTitle>Modifier</DialogTitle>
      <DialogContent>
        <Paper elevation={3} sx={{ padding: 1, backgroundColor: "#2d2d2d" }}>
          <CodeMirror
            value={tempQuery} // Utilisation de l'état local temporaire
            extensions={[sql()]} // Pour la coloration syntaxique SQL
            theme={materialDark} // Le thème sombre
            options={{ lineNumbers: true }} // Activer les numéros de ligne
            onChange={(value) => setTempQuery(value)} // Mise à jour de l'état temporaire
          />
        </Paper>
      </DialogContent>
      <DialogActions sx={{ mt: 1 }}>
        <Button onClick={onClose} variant="text" sx={{
          color: "#4E4B66", fontFamily: 'Inter, Roboto !important', textTransform: 'capitalize', fontSize: '11px', paddingLeft: '5%', boxShadow: 'none',
        }}>
          Annuler
        </Button>

        <Button
          onClick={handleSave}
          sx={{
            boxShadow: 'none',
            bgcolor: COLORS.PINK_INTELCIA,
            borderRadius: '50px',
            fontSize: "12px",
            textTransform: 'capitalize',
            fontFamily: 'Inter, Roboto !important',
            width: '10%',
            marginRight: '5px',
            '&:hover': {
              cursor: 'pointer',
            },
          }} variant="contained" >
          Modifier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RenderTableFromJSON = ({ data, onSendMessage, handleCallEdition }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showQuery, setShowQuery] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [query, setQuery] = useState(data.query || "");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const tableRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const contentArray = Array.isArray(data.content) ? data.content : [];
  const sortedContentArray = [...contentArray].sort((a, b) => {
    if (sortConfig.key) {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  const rowsToDisplay = sortedContentArray.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const exportToExcel = () => {
    if (!contentArray.length) return;

    const headers = Object.keys(contentArray[0]);
    const worksheetData = [
      headers,
      ...contentArray.map((row) => Object.values(row))
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Données');
    XLSX.writeFile(workbook, 'table-data.xlsx');
  };

  const checkFullscreenStatus = () => {
    setIsFullScreen(!!document.fullscreenElement);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', checkFullscreenStatus);
    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreenStatus);
    };
  }, []);

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (tableRef.current.requestFullscreen) {
        tableRef.current.requestFullscreen();
      } else if (tableRef.current.mozRequestFullScreen) {
        tableRef.current.mozRequestFullScreen();
      } else if (tableRef.current.webkitRequestFullscreen) {
        tableRef.current.webkitRequestFullscreen();
      } else if (tableRef.current.msRequestFullscreen) {
        tableRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const toggleQueryView = () => {
    setShowQuery(!showQuery);
  };

  if (!contentArray.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2">No table data available</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ border: '1px solid #F7F7FC', borderRadius: '4px', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: { xs: '8px 8px', sm: '8px 16px' },
            backgroundColor: '#EFF0F6',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
          }}
        >
          <Typography variant="h6" component="div" sx={{ mr: '5%' }}>
            {!showQuery ? '' : 'SQL Query'}
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: '4px', sm: '8px' }, flexWrap: 'nowrap' }}>
            <IconButton size="small" title="Filtrer">
              <TuneIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
                Filter
              </Typography>
            </IconButton>
            <IconButton size="small" title="Exporter en Excel" onClick={exportToExcel}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" title="Edit" onClick={() => setOpenModal(true)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" title="SQL" onClick={toggleQueryView}>
              {!showQuery ? <CodeIcon fontSize="small" /> : <CodeOffIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              title={isFullScreen ? 'Quitter le plein écran' : 'Plein écran'}
              onClick={toggleFullScreen}
            >
              {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" title="Plus">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {!showQuery ? (
          <Box sx={{ overflow: 'auto' }} ref={tableRef}>
            <Table sx={{ bgcolor: '#F7F7FC', width: '100%' }}>
              <TableHead>
                <TableRow>
                  {contentArray.length > 0 &&
                    Object.keys(contentArray[0]).map((key) => (
                      <TableCell
                        key={key}
                        sx={{ color: '#000', fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => handleSort(key)}
                      >
                        {key}
                        {sortConfig.key === key && (
                          <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                        )}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsToDisplay.map((row, index) => (
                  <TableRow key={index}>
                    {Object.values(row).map((value, idx) => (
                      <TableCell key={idx}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box ref={tableRef} sx={{ backgroundColor: '#1E1E1E', padding: 0, maxWidth: '900px' }}>
            <SyntaxHighlighter
              language="sql"
              style={dracula}
              wrapLongLines
              customStyle={{ margin: 0, borderRadius: 0 }}
            >
              {data.query || "No SQL query available"}
            </SyntaxHighlighter>
          </Box>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={contentArray.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #F7F7FC' }}
        />
      </Box>
      <QueryEditorModal
        initialQuery={query}
        onUpdate={(newQuery) => setQuery(newQuery)}
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSendMessage={onSendMessage}
        handleCallEdition={handleCallEdition}
      />
    </>
  );
}
const ChatMessage = ({ message, isUser, isLoading, timestamp, onSendMessage, isFirstBotMessage, isLastBotMessage, lastUserMessage, handleCallEdition }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  let parsedMessage;
  try {
    parsedMessage = typeof message === 'string' ? JSON.parse(message) : message;
  } catch (e) {
    parsedMessage = { type: "text", content: message };
  }

  //   const rotateAnimation = keyframes`
  //   0% { transform: rotate(0deg); }
  //   50% { transform: rotate(180deg); } /* Tourne à moitié */
  //   100% { transform: rotate(0deg); }  /* Retour à la position initiale */
  // `;

  const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  50% { transform: rotate(900deg); } /* 2,5 tours (360° * 2,5) */
  100% { transform: rotate(0deg); }  /* Retour à la position initiale */
`;

  const renderContent = () => {
    let parsedMessages;
    try {
      const parsed = typeof message === 'string' ? JSON.parse(message) : message;
      parsedMessages = Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      parsedMessages = [{ type: "text", content: message }];
    }
    return parsedMessages.map((msg, idx) => {
      switch (msg.type) {
        case "table":
          return <RenderTableFromJSON key={idx} data={msg} onSendMessage={onSendMessage} handleCallEdition={handleCallEdition} />;
        case "action":
          if (Array.isArray(msg.content)) {
            return (
              <Box key={idx}>
                <Typography variant="button" gutterBottom sx={{ display: 'block', color: COLORS.GRAY, fontWeight: 600, fontSize: 11 }}>
                  FOLLOW-UP
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {msg.content.map((item, i) => (
                    <Button
                      onClick={() => onSendMessage(item.question)}
                      key={i}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: '16px',
                        textTransform: 'none',
                        color: COLORS.BLACK_TEXT,
                        border: `1px solid ${COLORS.GRAY_BORDER}`,
                        '&:hover': { backgroundColor: COLORS.GRAY_HOVER },
                      }}
                    >
                      {item.question}
                    </Button>
                  ))}
                </Box>
              </Box>
            );
          }
          return (
            <Box key={idx} sx={{ backgroundColor: '#1E1E1E', color: '#FFFFFF', padding: 2, borderRadius: 1, overflowX: 'auto' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0 }}>
                {msg.content}
              </Typography>
            </Box>
          );

        default:
          return (
            <Typography key={idx} variant="body2" color={COLORS.BLACK_TEXT} sx={{ whiteSpace: 'pre-wrap', overflowY: 'auto', maxHeight: '200px' }}>
              {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
            </Typography>
          );
      }
    });
  };


  const extractPlainText = (message) => {
    let parsedMessages;

    try {
      const parsed = typeof message === 'string' ? JSON.parse(message) : message;
      parsedMessages = Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      parsedMessages = [{ type: "text", content: message }];
    }


    // Si le message n'a pas de type ou de contenu, retourner le message tel quel
    if (!parsedMessages || !parsedMessages.type) {
      return message;
    }

    // Pour le type "text" ou le cas par défaut, retourner uniquement le contenu textuel
    if (parsedMessages.type === "text" || !parsedMessages.type) {
      return typeof parsedMessages.content === 'string'
        ? parsedMessages.content
        : message;
    }

    // Pour les autres types, retourner le message original
    return message;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCopyToClipboard = () => {
    const textToCopy = extractPlainText(message);
    console.log('msg:', message);

    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error("Erreur lors de la copie : ", err);
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 2,
        width: "100%",
      }}
    >
      {!isUser && isFirstBotMessage && (
        <Box
          component="img"
          src={MEDIAS.sparkles_auth}
          alt="Sparkles"
          sx={{
            mr: 1, // équivalent à marginRight: 16 (selon ton thème MUI)
            height: "30px",
            width: "36px",
            objectFit: "contain",
            // animation: isLoading ? `${rotateAnimation} 0.6s ease-in-out infinite` : "none",
            animation: isLoading ? `${rotateAnimation} 3s ease-in-out infinite alternate` : "none",

          }}
        // {...props}
        />
      )
      }
      {isLoading ? (
        <CustomSkeleton />
      ) : (
        <Paper
          sx={{
            // gap: '16px',
            p: '10px',
            backgroundColor: isUser ? COLORS.GRAY_BG : COLORS.WHITE,
            maxWidth: { xs: "90%", sm: "80%", md: "70%" },
            width: parsedMessage?.type === "table" ? "100%" : "auto",
            borderRadius: '15px',
            boxShadow: 'none',
            wordWrap: 'break-word',
            overflow: 'hidden',
          }}
        >
          {renderContent()}
          {
            isUser ?
              <Typography
                variant="caption"
                sx={{
                  color: COLORS.GRAY,
                  display: "flex",
                  justifyContent: 'flex-end',
                  // mt: 1,
                }}
              >
                {timestamp}
              </Typography>
              : !isUser && isLastBotMessage ?
                <Toolbar onSendMessage={onSendMessage} userMessage={lastUserMessage} handleCopyToClipboard={handleCopyToClipboard}
                />
                : null

          }
        </Paper>

      )}
      {isUser && (
        <Avatar sx={{ width: 24, height: 24, ml: 2 }} src={MEDIAS.walid_pic} />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Texte copié dans le presse-papier
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ChatComponent = ({ messages, isLoading, onSendMessage, lastUserMessage, handleCallEdition }) => {
  const messagesEndRef = useRef(null); // Référence pour le dernier message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };


  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  return (
    <Box sx={{ width: "100%", maxWidth: '90%', mx: "auto", p: 2, mb: { xs: '12%', sm: '6%' } }}>
      {messages?.map((msg, index) => (
        <ChatMessage
          key={index}
          index={index}
          message={msg.text}
          isUser={msg.isUser}
          isLoading={msg.isLoading || (isLoading && index === messages.length - 1)}
          timestamp={msg.timestamp}
          onSendMessage={onSendMessage}
          isFirstBotMessage={msg.isFirstBotMessage}
          isLastBotMessage={msg.isLastBotMessage}
          lastUserMessage={lastUserMessage}
          handleCallEdition={handleCallEdition}

        />
      ))}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatComponent;